import {
  Badge,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  Theme,
  IconButton,
  alpha,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { selectAuthenticated, selectEmail } from "redux/authSlice";
import { usePopover } from "hooks/use-popover";
import CustomPopper from "global/components/CustomPopper/CustomPopper";
import {
  calculateRelativeTime,
  isTruthy,
  handleErrorMessage,
} from "helpers/methods";
import {
  fetchInboxRequests,
  fetchInboxRequestsCount,
  getSignRequestDataByToken,
} from "screens/MySignRequests/MySignRequestService";
import { isRequestDeclined } from "screens/MySignRequests/Inbox/InboxRowHelper";
import { SignRequest, SortingConfig } from "models/interfaces";
import strings from "global/constants/StringConstants";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { InboxNavIcon } from "utils/TopNavIcons";
import { topNavIconButtonSx } from "layouts/DrawerLayout/components/TopNav.styles";
import { topNav } from "theme/colors";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { handleSignNow } from "screens/MySignRequests/MySignRequestHelper";
import { panel } from "theme/colors";
import { downloadFilesAsZip } from "screens/SignRequest/ParticipantScreen/ParticipantScreenService";
import { setLoading } from "redux/appSlice";
import EmptyState from "global/components/EmptyState/EmptyState";

const truncateSubject = (subject: string, limit: number = 60) => {
  if (!subject) return "Unnamed Document";
  if (subject.length <= limit) return subject;

  const extensionMatch = subject.match(/\.[a-zA-Z0-9]+$/);
  if (extensionMatch) {
    const ext = extensionMatch[0];
    const base = subject.slice(0, -ext.length);
    const maxBaseLength = limit - ext.length - 3;
    if (maxBaseLength > 0) {
      return base.slice(0, maxBaseLength) + "..." + ext;
    }
  }
  return subject.slice(0, limit - 3) + "...";
};

interface InboxRequestItemProps {
  request: SignRequest;
  userEmail: string;
  /** Rows alternate fill, as in the reference. */
  striped: boolean;
}

const InboxRequestItem: React.FC<InboxRequestItemProps> = ({
  request,
  userEmail,
  striped,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const isPending = request.status === strings.pending;
  const isCurrentSigner =
    request.currentSigners?.includes(userEmail) &&
    isPending &&
    !isRequestDeclined(request);
  const isSigned =
    request.status === strings.signed || request.status === "Signed";

  const requestedByName = request.requestedBy?.name;

  // Completed requests get the green document tile; anything still in flight
  // keeps the brand peach.
  const tileBg = isSigned
    ? theme.palette.notification.success.bg
    : theme.palette.signer.orange.tint;
  const tileColor = isSigned
    ? theme.palette.notification.success.icon
    : theme.palette.signer.orange.main;

  const handleDownload = async () => {
    try {
      dispatch(setLoading(true));
      const data = await getSignRequestDataByToken(request.requestToken);
      downloadFilesAsZip(data, (value: boolean) => {
        dispatch(setLoading(value));
      });
    } catch (error: any) {
      dispatch(setLoading(false));
      handleErrorMessage(error);
    }
  };

  const actionButtonSx = {
    width: 36,
    height: 36,
    borderRadius: "10px",
    border: `1px solid ${theme.palette.signer.border.main}`,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.signer.orange.main,
    "& svg": { fontSize: 18 },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderColor: theme.palette.signer.orange.border,
    },
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: "20px",
        py: "12px",
        backgroundColor: striped ? panel.stripe : theme.palette.common.white,
        "&:hover": { backgroundColor: panel.hover },
      }}
    >
      {/* Document tile on the left, tinted by status */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: tileBg,
          color: tileColor,
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>

      {/* Text Info in the middle */}
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: 1.35,
            color: panel.title,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {truncateSubject(request.emailSubject)}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            color: panel.metaText,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {requestedByName} &middot; {calculateRelativeTime(request.createdOn)}
        </Typography>
      </Stack>

      {/* Action Button/Icon on Right */}
      <Box sx={{ flexShrink: 0 }}>
        {isCurrentSigner && (
          <Tooltip title="Sign Now" placement="top" arrow>
            <IconButton
              onClick={() => handleSignNow(request.requestToken)}
              aria-label={`Sign now: ${truncateSubject(request.emailSubject)}`}
              sx={actionButtonSx}
            >
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        {isSigned && (
          <Tooltip title="Download documents" placement="top" arrow>
            <IconButton
              onClick={handleDownload}
              aria-label={`Download documents: ${truncateSubject(
                request.emailSubject,
              )}`}
              sx={actionButtonSx}
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};

/**
 * Placeholder row. Mirrors `InboxRequestItem`'s geometry exactly — same
 * padding, same 40px tile, same two text lines, same 36px action slot — so the
 * list doesn't jump when the real rows land.
 */
const InboxRequestSkeleton: React.FC<{ striped: boolean }> = ({ striped }) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: "20px",
        py: "12px",
        backgroundColor: striped ? panel.stripe : theme.palette.common.white,
      }}
    >
      <Skeleton
        variant="rounded"
        animation="wave"
        width={40}
        height={40}
        sx={{ borderRadius: "12px", flexShrink: 0, bgcolor: panel.skeleton }}
      />
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton
          variant="text"
          animation="wave"
          width="70%"
          height={14}
          sx={{ bgcolor: panel.skeleton }}
        />
        <Skeleton
          variant="text"
          animation="wave"
          width="45%"
          height={12}
          sx={{ bgcolor: panel.skeleton }}
        />
      </Stack>
      <Skeleton
        variant="rounded"
        animation="wave"
        width={36}
        height={36}
        sx={{ borderRadius: "10px", flexShrink: 0, bgcolor: panel.skeleton }}
      />
    </Stack>
  );
};

const Inbox = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const userEmail = useAppSelector(selectEmail);
  const [requests, setRequests] = useState<SignRequest[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  // Gates the cold-load placeholders. A plain "is fetching" flag isn't enough:
  // it reads false until the mount effect fires, which would let the empty
  // state paint for a frame before the first request goes out.
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  const inboxPopover = usePopover<any>();

  const sortingConfigs: SortingConfig[] = useMemo(
    () => [
      {
        entity: "signRequest",
        order: "DESC",
        field: "createdOn",
      },
    ],
    [],
  );

  const fetchInboxData = async () => {
    if (!isLoggedIn) return;
    try {
      const data = await fetchInboxRequests(1, sortingConfigs, "All", "");
      const count = await fetchInboxRequestsCount(strings.pending, "");
      setRequests(data || []);
      setPendingCount(Number(count) || 0);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchInboxData();
      const interval = setInterval(fetchInboxData, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (inboxPopover.open && isLoggedIn) {
      fetchInboxData();
    }
  }, [inboxPopover.open]);

  const renderRequests = () => {
    // Only on a cold load — the 30s refresh and the on-open refetch both run
    // against rows that are already on screen, and swapping those for
    // placeholders would flash the panel every half minute.
    if (!hasLoaded && requests.length === 0) {
      return (
        <Stack>
          {[0, 1, 2].map((index) => (
            <InboxRequestSkeleton key={index} striped={index % 2 === 0} />
          ))}
        </Stack>
      );
    }

    if (!isTruthy(requests) || requests.length === 0) {
      return (
        /* A popover widget — the compact size: no icon, no buttons. */
        <EmptyState size="compact" title="No requests in your inbox" />
      );
    }

    return (
      <Stack>
        {requests.slice(0, 3).map((request, index) => (
          <InboxRequestItem
            key={request.id || index}
            request={request}
            userEmail={userEmail}
            striped={index % 2 === 0}
          />
        ))}
      </Stack>
    );
  };

  return (
    <>
      <Tooltip title="Inbox" placement="bottom" arrow>
        <Box
          component="button"
          type="button"
          aria-haspopup="dialog"
          aria-expanded={inboxPopover.open}
          aria-label={
            pendingCount > 0
              ? `Inbox, ${pendingCount} ${
                  pendingCount === 1 ? "request" : "requests"
                } awaiting you`
              : "Inbox"
          }
          sx={topNavIconButtonSx}
          onClick={inboxPopover.handleOpen}
          ref={inboxPopover.anchorRef}
        >
          <Badge
            badgeContent={pendingCount > 0 ? pendingCount : 0}
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: topNav.badge,
                color: theme.palette.common.white,
                fontSize: "10px",
                fontWeight: 700,
                top: "2px",
                right: "2px",
                height: "16px",
                minWidth: "16px",
                padding: "0 4px",
                border: `2px solid ${theme.palette.common.white}`,
              },
            }}
          >
            <InboxNavIcon sx={{ fontSize: 20 }} />
          </Badge>
        </Box>
      </Tooltip>

      <CustomPopper
        open={inboxPopover.open}
        anchorRef={inboxPopover.anchorRef.current}
        handleClose={inboxPopover.handleClose}
        ariaLabel="Inbox"
        width={isDesktop ? "25rem" : "25rem"}
        // Rows and footer run edge to edge, so the panel carries no padding.
        padding={0}
        shadow={panel.shadow}
      >
        <Stack>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              px: "20px",
              py: "16px",
              borderBottom: `1px solid ${panel.border}`,
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: "17px",
                fontWeight: 700,
                color: panel.title,
              }}
            >
              Inbox
            </Typography>
            {pendingCount > 0 && (
              <Box
                sx={{
                  backgroundColor: panel.accentPillBg,
                  color: panel.accentPillText,
                  padding: "4px 10px",
                  borderRadius: "999px",
                  fontSize: "12px",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {pendingCount} awaiting you
              </Box>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <IconButton
              aria-label="Close inbox"
              onClick={() => inboxPopover.handleClose()}
              sx={{
                color: panel.metaText,
                p: "4px",
                "&:hover": {
                  backgroundColor: theme.palette.signer.surface.muted,
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Stack>
          <Stack
            sx={{ maxHeight: "350px", overflowY: "auto" }}
            aria-busy={!hasLoaded}
          >
            {renderRequests()}
          </Stack>
          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              p: "10px 12px",
              borderTop: `1px solid ${panel.border}`,
              backgroundColor: panel.stripe,
            }}
          >
            <Button
              size="small"
              variant="text"
              color="primary"
              endIcon={<ArrowRightAltIcon />}
              onClick={() => {
                inboxPopover.handleClose();
                history.push(viewpaths.inboxViewPath);
              }}
              sx={{
                fontWeight: 700,
                textTransform: "none",
                fontSize: "14px",
                py: "8px",
                px: "20px",
                borderRadius: "10px",
                color: "primary.main",
                "& .MuiButton-endIcon": {
                  color: "primary.main",
                },
                "&:hover": {
                  backgroundColor: theme.palette.signer.orange.hover,
                },
              }}
            >
              View all requests
            </Button>
          </Box>
        </Stack>
      </CustomPopper>
    </>
  );
};

export default Inbox;
